import React, { useState } from 'react';
import Breadcrumbs from '@ajat/design-system/Breadcrumbs';
import { ComponentPage, DemoCard } from '../shared';

export const meta = {
  component: Breadcrumbs,
  title: 'Breadcrumbs',
  group: 'Navigation',
  importPath: '@ajat/design-system/Breadcrumbs',
  figmaNode: '2699:17501',
  description:
    'The trail above a page title: DS Hyperlinks (variant small = A.Small, gray-6) separated by a gray-4 "/" (P.Regular). The last crumb is the current page (aria-current="page"); a crumb with neither href nor onClick is plain text. A click-only crumb never follows Hyperlink\'s default "#". Colours are hooks an ancestor sets — --breadcrumbs-link-color, --breadcrumbs-link-hover, --breadcrumbs-link-focus, --breadcrumbs-separator-color (SecondaryBannerV2 theme="dark" uses them).',
  props: [
    ['items', 'array', '[]', '[{ label, href?, onClick? }], first crumb first; the last one is the current page. Empty → nothing renders'],
    ['ariaLabel', 'string', "'Breadcrumb'", 'Accessible name of the nav landmark — localise it per site'],
    ['className', 'string', "''", 'Additional CSS class on the nav'],
  ],
  usage: [
    {
      title: 'Links',
      code: `import Breadcrumbs from '@ajat/design-system/Breadcrumbs';

<Breadcrumbs
  ariaLabel="Brödsmulor"
  items={[
    { label: 'HEM', href: '/' },
    { label: 'EDITIONS', href: '/editions' },
  ]}
/>`,
    },
    {
      title: 'Router navigation',
      code: `// onClick without href: the crumb never follows "#"
<Breadcrumbs items={[{ label: 'HEM', onClick: () => navigate('/') }, { label: 'EDITIONS' }]} />`,
    },
    {
      title: 'On a dark surface',
      code: `.my-dark-surface {
  --breadcrumbs-link-color: var(--color-gray-2);
  --breadcrumbs-link-hover: var(--color-gray-0);
  --breadcrumbs-link-focus: var(--color-gray-0);
  --breadcrumbs-separator-color: var(--color-gray-5);
}`,
    },
  ],
};

const DARK_SURFACE = {
  padding: 'var(--space-ui-medium-m)',
  background: 'var(--color-gray-975)',
  '--breadcrumbs-link-color': 'var(--color-gray-2)',
  '--breadcrumbs-link-hover': 'var(--color-gray-0)',
  '--breadcrumbs-link-focus': 'var(--color-gray-0)',
  '--breadcrumbs-separator-color': 'var(--color-gray-5)',
};

export default function BreadcrumbsPage() {
  const [clicked, setClicked] = useState(null);

  return (
    <ComponentPage meta={meta}>
      <DemoCard title="Links" hint="the last crumb is the current page">
        <Breadcrumbs items={[{ label: 'HEM', href: '#' }, { label: 'EDITIONS', href: '#' }]} />
      </DemoCard>

      <DemoCard title="Current page as text" hint="no href / onClick on the last crumb">
        <Breadcrumbs
          items={[
            { label: 'HEM', href: '#' },
            { label: 'EDITIONS', href: '#' },
            { label: 'RÖD EDITION' },
          ]}
        />
      </DemoCard>

      <DemoCard title="Click-only crumbs" hint={clicked ? `clicked: ${clicked}` : 'router navigation — the URL stays put'}>
        <Breadcrumbs
          items={[
            { label: 'HEM', onClick: () => setClicked('HEM') },
            { label: 'EDITIONS', onClick: () => setClicked('EDITIONS') },
          ]}
        />
      </DemoCard>

      <DemoCard title="Dark surface" hint="colour hooks set by the surrounding element" flush>
        <div style={DARK_SURFACE}>
          <Breadcrumbs items={[{ label: 'HEM', href: '#' }, { label: 'EDITIONS', href: '#' }]} />
        </div>
      </DemoCard>
    </ComponentPage>
  );
}
